import {
  Button,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Typography
} from "@material-ui/core";
import FormSpacer from "@saleor/components/FormSpacer";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { useTaskAreaUpdate } from "@saleor/dropOff/mutation";
import { useGetTemplateQuery } from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import {
  GetTemplate_template_dropOffLocations_edges_node,
  GetTemplate_template_taskAreas_edges_node
} from "@saleor/dropOff/types/GetTemplate";
import { TemplateAction } from "@saleor/dropOff/utils/handlers";
import useListSettings from "@saleor/hooks/useListSettings";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { ListViews, Pagination } from "@saleor/types";
import { LogisticsStatusEnum, StoreTypeEnum } from "@saleor/types/globalTypes";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import * as React from "react";

import DropOffAssignStores from "./DropOffAssignStores";
import DropOffTaskAreaCard from "./DropOffTaskAreaCard";

interface DropOffManageTaskAreasProps {
  disableRename: boolean;
  disableAddRemove: boolean;
  mallId: string;
  templateId: string;
  templateAction: TemplateAction;
  prevStep?: () => void;
  handleFinishSetup?: () => void;
  clickBackForEditTemplate?: (templateId: string, mallId: string) => void;
}

const DropOffManageTaskAreas: React.FC<DropOffManageTaskAreasProps> = props => {
  const {
    disableRename,
    disableAddRemove,
    mallId,
    templateId,
    templateAction,
    prevStep,
    handleFinishSetup,
    clickBackForEditTemplate
  } = props;

  const classes = useTemplateStyles(props);
  const notify = useNotifier();
  const paginate = usePaginator();

  const [homeLocations, setHomeLocations] = React.useState<
    GetTemplate_template_dropOffLocations_edges_node[]
  >([]);
  const [selectedHomeLocation, setSelectedHomeLocation] = React.useState(null);
  const [listTaskAreas, setListTaskAreas] = React.useState<
    GetTemplate_template_taskAreas_edges_node[]
  >([]);
  const [showAssignStoreDialog, setShowAssignStoreDialog] = React.useState<
    boolean
  >(false);
  const [selectedTaskArea, setSelectedTaskArea] = React.useState(null);
  const [selectedTaskAreaIndex, setSelectedTaskAreaIndex] = React.useState(0);

  const [searchTemplateTA, setSearchTemplateTA] = React.useState("");
  const [filterTemplateTA, setFilterTemplateTA] = React.useState<StoreTypeEnum>(
    null
  );

  const [
    isSelectAllAvailableStores,
    setIsSelectAllAvailableStores
  ] = React.useState<boolean>(false);
  const [
    isSelectAllAssignedStores,
    setIsSelectAllAssignedStores
  ] = React.useState<boolean>(false);

  const [templateParams, setTemplateParams] = React.useState<Pagination>({
    after: null,
    before: null
  });

  const {
    updateListSettings: templateUpdateListSettings,
    settings: templateSettings
  } = useListSettings(ListViews.DISPATCH_LIST);

  const {
    data: template,
    refetch,
    loading: templateLoading
  } = useGetTemplateQuery({
    displayLoader: true,
    skip: !templateId || templateId.length === 0,
    variables: {
      after: templateParams?.after ? templateParams.after : null,
      before: templateParams?.before ? templateParams.before : null,
      first: templateParams?.before ? null : templateSettings.rowNumber,
      id: templateId,
      last: templateParams?.before ? templateSettings.rowNumber : null,
      tenantSearch: searchTemplateTA,
      tenantStoreType: filterTemplateTA
    }
  });

  const paginationState = createPaginationState(
    templateSettings.rowNumber,
    templateParams
  );

  const { pageInfo: templatePageInfo } = paginate(
    maybe(
      () =>
        template.template.taskAreas.edges[selectedTaskAreaIndex].node.tenants
          .pageInfo
    ),
    paginationState,
    templateParams
  );

  const onTemplateLoadNextPage = () => {
    setTemplateParams({
      after: templatePageInfo.endCursor,
      before: null
    });
    if (isSelectAllAssignedStores) {
      setIsSelectAllAssignedStores(false);
    }
    refetch();
  };

  const onTemplateLoadPreviousPage = () => {
    setTemplateParams({
      after: null,
      before: templatePageInfo.startCursor
    });
    if (isSelectAllAssignedStores) {
      setIsSelectAllAssignedStores(false);
    }
    refetch();
  };

  const [updateTaskArea] = useTaskAreaUpdate({
    onCompleted: e => {
      if (e.taskAreaUpdate.taskAreaError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        setTemplateParams({
          after: null,
          before: null
        });
        refetch();
      } else {
        notify({
          status: "error",
          text: e.taskAreaUpdate.taskAreaError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const handleRenameSave = (taskAreaId, newTaskAreaName) => {
    updateTaskArea({
      variables: {
        id: taskAreaId,
        input: {
          name: newTaskAreaName
        }
      }
    });
  };

  React.useEffect(() => {
    if (template && (template?.template?.taskAreas?.edges ?? [])) {
      const mergedTaskAreas = [];

      for (const ta of template?.template?.taskAreas?.edges ?? []) {
        if (ta.node.dropOffLocation.id === selectedHomeLocation) {
          mergedTaskAreas.push(ta.node);
        }
      }

      setListTaskAreas(mergedTaskAreas);
    }
  }, [template?.template?.taskAreas, selectedHomeLocation]);

  React.useEffect(() => {
    if (template?.template?.dropOffLocations?.edges) {
      const homelocs = template?.template?.dropOffLocations?.edges.map(
        edge => edge.node
      );
      setHomeLocations(homelocs);
    }
  }, [template?.template?.dropOffLocations]);

  React.useEffect(() => {
    if (homeLocations && homeLocations.length > 0 && choices.length > 0) {
      const homeloc = homeLocations.find(e => e.defaultDropOffLocation === true)
        ?.id;
      setSelectedHomeLocation(homeloc);
    }
  }, [homeLocations]);

  const choices = homeLocations?.map(e => ({
    label: e.name,
    value: e.id
  }));

  const handleOpenModal = (ta, index) => {
    setSelectedTaskArea(ta);
    setSelectedTaskAreaIndex(index);

    setTemplateParams({
      after: null,
      before: null
    });
    setFilterTemplateTA(null);
    setSearchTemplateTA("");
    setIsSelectAllAvailableStores(false);
    setIsSelectAllAssignedStores(false);

    setShowAssignStoreDialog(true);
  };

  const handleCloseModal = () => {
    setTemplateParams({
      after: null,
      before: null
    });
    setFilterTemplateTA(null);
    setSearchTemplateTA("");
    setIsSelectAllAvailableStores(false);
    setIsSelectAllAssignedStores(false);

    setShowAssignStoreDialog(false);
  };

  return (
    <Container>
      <div className={classes.GeneralInformationRoot}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Container>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    className={classes.dropOffCardAccordionTitle}
                  >
                    Choose Home Location
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <SingleSelectField
                    name={"selectedHomeLocation"}
                    value={selectedHomeLocation}
                    selectProps={{
                      fullWidth: true
                    }}
                    label="Home Location"
                    choices={choices}
                    onChange={e => {
                      setSelectedHomeLocation(e.target.value);
                    }}
                  />
                </Grid>
              </Grid>
              <FormSpacer spacing={4} />

              <Grid container spacing={2}>
                {listTaskAreas.map((ta, index) => {
                  if (ta.status !== LogisticsStatusEnum.DELETED) {
                    return (
                      <Grid key={ta.id} item xs={6}>
                        <DropOffTaskAreaCard
                          key={"taskAreaCard" + ta.id}
                          disableRename={disableRename}
                          disableAddRemove={disableAddRemove}
                          taskAreaId={ta.id}
                          taskAreaName={ta.name}
                          tenants={ta?.tenants}
                          templateAction={templateAction}
                          onClickedAddRemove={() => handleOpenModal(ta, index)}
                          onRename={(taskAreaId, newTaskAreaName) => {
                            handleRenameSave(taskAreaId, newTaskAreaName);
                          }}
                        />
                      </Grid>
                    );
                  }
                })}
              </Grid>

              {templateAction === TemplateAction.CREATE ? (
                <div
                  className={`${classes.displayFlex} ${classes.displayFlexPaddingTop50}`}
                >
                  <Button
                    color="primary"
                    variant="outlined"
                    className={classes.displayFlexMarginLeft}
                    onClick={() => prevStep()}
                  >
                    <div className={classes.textBackButton}>BACK</div>
                  </Button>

                  <Button
                    variant="contained"
                    className={`${classes.buttonColor} ${classes.displayFlexMarginLeft20}`}
                    onClick={() => handleFinishSetup()}
                  >
                    <div className={classes.textButton}>SAVE</div>
                  </Button>
                </div>
              ) : (
                <div
                  className={`${classes.displayFlex} ${classes.displayFlexPaddingTop50}`}
                >
                  <Button
                    color="primary"
                    variant="outlined"
                    className={classes.displayFlexMarginLeft}
                    onClick={() => clickBackForEditTemplate(templateId, mallId)}
                  >
                    <div className={classes.textBackButton}>BACK</div>
                  </Button>
                </div>
              )}
            </Container>
          </Grid>
        </Grid>

        {/* ASSIGN STORES DIALOG */}
        <Dialog
          open={showAssignStoreDialog}
          onClose={() => handleCloseModal()}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="lg"
        >
          <DialogContent
            style={
              templateAction === TemplateAction.VIEW
                ? { height: 750, width: 490 }
                : { height: 800, width: 1000 }
            }
          >
            <DropOffAssignStores
              key={"assignStoresEdit-" + mallId + "-" + templateId}
              selectedTaskArea={selectedTaskArea}
              mallId={mallId}
              templateId={templateId}
              templateTaskAreas={template?.template?.taskAreas?.edges}
              refetchTemplate={refetch}
              setTemplateParams={setTemplateParams}
              searchAssignedStores={searchTemplateTA}
              setSearchAssignedStores={setSearchTemplateTA}
              setFilterAssignedStores={setFilterTemplateTA}
              templateLoading={templateLoading}
              templateSettings={templateSettings}
              templateUpdateListSettings={templateUpdateListSettings}
              templatePageInfo={templatePageInfo}
              onTemplateLoadNextPage={onTemplateLoadNextPage}
              onTemplateLoadPreviousPage={onTemplateLoadPreviousPage}
              onClose={() => handleCloseModal()}
              isSelectAllAvailableStores={isSelectAllAvailableStores}
              setIsSelectAllAvailableStores={setIsSelectAllAvailableStores}
              isSelectAllAssignedStores={isSelectAllAssignedStores}
              setIsSelectAllAssignedStores={setIsSelectAllAssignedStores}
              templateAction={templateAction}
            />

            {templateAction === TemplateAction.VIEW ? (
              <></>
            ) : (
              <div
                className={classes.divFlexEnd100}
                style={{
                  paddingRight: "30px",
                  paddingTop: "10px"
                }}
              >
                <Button
                  className={classes.buttonColor}
                  variant="contained"
                  onClick={() => handleCloseModal()}
                >
                  <div className={classes.textButton}>DONE</div>
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* ASSIGN STORES DIALOG */}
      </div>
    </Container>
  );
};

export default DropOffManageTaskAreas;
